import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';
import {
  MessageSquare,
  Star,
  Send,
  ChevronDown,
  ChevronRight,
  Loader2,
  Sparkles,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { NoteContent } from '@/components/NoteContent';
import { LoginArea } from '@/components/auth/LoginArea';

import { useComments } from '@/hooks/useComments';
import { usePostComment } from '@/hooks/usePostComment';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { genUserName } from '@/lib/genUserName';
import type { Skill } from '@/lib/skills';

interface SkillReviewsProps {
  skill: Skill;
}

export function SkillReviews({ skill }: SkillReviewsProps) {
  const { data: commentsData, isLoading, error } = useComments(skill.event, 500);
  const reviews = commentsData?.topLevelComments || [];

  if (error) {
    return (
      <Card className="glass-card">
        <CardContent className="py-12 text-center">
          <MessageSquare className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">Failed to load reviews</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="w-5 h-5 text-orange-400" />
          Reviews
          {!isLoading && (
            <span className="text-sm font-normal text-muted-foreground">
              ({reviews.length})
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Review Form */}
        <ReviewForm skill={skill} />

        {/* Reviews List */}
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(2)].map((_, i) => (
              <ReviewSkeleton key={i} />
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <EmptyReviews skillName={skill.name} />
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <ReviewItem
                key={review.id}
                review={review}
                skill={skill}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface ReviewFormProps {
  skill: Skill;
  replyTo?: NostrEvent;
  onSuccess?: () => void;
  compact?: boolean;
}

function ReviewForm({ skill, replyTo, onSuccess, compact = false }: ReviewFormProps) {
  const [content, setContent] = useState('');
  const { user } = useCurrentUser();
  const { mutate: postComment, isPending } = usePostComment();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || !user) return;

    postComment(
      {
        content: content.trim(),
        root: skill.event,
        reply: replyTo,
      },
      {
        onSuccess: () => {
          setContent('');
          onSuccess?.();
        },
      }
    );
  };

  if (!user) {
    return (
      <div className={`p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] ${compact ? '' : ''}`}>
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <MessageSquare className="w-4 h-4" />
            <span className="text-sm">Sign in to {replyTo ? 'reply' : 'write a review'}</span>
          </div>
          <LoginArea />
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className={`p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] focus-within:border-orange-500/30 transition-colors`}>
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={replyTo ? "Write a reply..." : `Share your experience with ${skill.name}...`}
          className={`bg-transparent border-0 shadow-none p-0 resize-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50 ${compact ? 'min-h-[60px]' : 'min-h-[80px]'}`}
          disabled={isPending}
        />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {replyTo ? 'Replying to review' : 'Your review will be visible to everyone'}
        </p>
        <Button
          type="submit"
          size="sm"
          disabled={!content.trim() || isPending}
          className="gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 border-0"
        >
          {isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {replyTo ? 'Reply' : 'Post Review'}
        </Button>
      </div>
    </form>
  );
}

interface ReviewItemProps {
  review: NostrEvent;
  skill: Skill;
  depth?: number;
}

function ReviewItem({ review, skill, depth = 0 }: ReviewItemProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(depth < 1);

  const author = useAuthor(review.pubkey);
  const { data: commentsData } = useComments(skill.event, 500);

  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? genUserName(review.pubkey);
  const npub = nip19.npubEncode(review.pubkey);
  const timeAgo = formatDistanceToNow(new Date(review.created_at * 1000), { addSuffix: true });

  // Get direct replies to this review
  const replies = commentsData?.getDirectReplies(review.id) || [];
  const hasReplies = replies.length > 0;

  return (
    <div className={`space-y-3 ${depth > 0 ? 'ml-6 pl-4 border-l-2 border-white/[0.06]' : ''}`}>
      <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <Link
            to={`/profile/${npub}`}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <Avatar className="w-8 h-8">
              <AvatarImage src={metadata?.picture} alt={displayName} />
              <AvatarFallback className="text-xs bg-gradient-to-br from-orange-500/20 to-amber-500/20">
                {displayName[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-medium">{displayName}</p>
              <p className="text-xs text-muted-foreground">{timeAgo}</p>
            </div>
          </Link>
        </div>

        {/* Content */}
        <div className="text-sm text-muted-foreground leading-relaxed">
          <NoteContent event={review} className="text-sm" />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/[0.04]">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowReplyForm(!showReplyForm)}
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <MessageSquare className="w-3 h-3 mr-1" />
            Reply
          </Button>

          {hasReplies && (
            <Collapsible open={showReplies} onOpenChange={setShowReplies}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  {showReplies ? (
                    <ChevronDown className="w-3 h-3 mr-1" />
                  ) : (
                    <ChevronRight className="w-3 h-3 mr-1" />
                  )}
                  {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          )}
        </div>
      </div>

      {/* Reply Form */}
      {showReplyForm && (
        <div className="ml-6">
          <ReviewForm
            skill={skill}
            replyTo={review}
            onSuccess={() => setShowReplyForm(false)}
            compact
          />
        </div>
      )}

      {/* Replies */}
      {hasReplies && (
        <Collapsible open={showReplies} onOpenChange={setShowReplies}>
          <CollapsibleContent className="space-y-3">
            {replies.map((reply) => (
              <ReviewItem
                key={reply.id}
                review={reply}
                skill={skill}
                depth={depth + 1}
              />
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

function EmptyReviews({ skillName }: { skillName: string }) {
  return (
    <div className="py-10 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-500/10 to-amber-500/10 border border-white/[0.06] flex items-center justify-center">
        <Sparkles className="w-8 h-8 text-orange-400/50" />
      </div>
      <h3 className="font-medium mb-2">No reviews yet</h3>
      <p className="text-sm text-muted-foreground max-w-xs mx-auto">
        Be the first to share your experience with {skillName}. Your feedback helps the community!
      </p>
    </div>
  );
}

function ReviewSkeleton() {
  return (
    <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
      <div className="flex items-center gap-3 mb-3">
        <Skeleton className="w-8 h-8 rounded-full" />
        <div className="space-y-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </div>
  );
}
